<template>
  <Card :title="$t('invoice.list')" containerClass="mb-5">
    <template #cardToolbar>
      <div class="d-flex justify-content-end align-items-center">
        <DateRangePicker id="invoice_date_id" @update:date="handleRangeDate" />
        <ButtonDropdownFilter
            :search="false"
            :options="invoiceStatus"
            value-field="value"
            :name-field="locale"
            v-model="query.status"
        />
        <Button v-if="$can(abilities.CREATE_INVOICE)" type="new" :isDisabled="pipeline?.status == 'WON' || pipeline?.status == 'LOST' || pipeline.status == 'ARCHIVED'" btn-class="btn-sm me-2" btn-text="invoices.create" @click="openCreateView" ></Button>
      </div>
    </template>
    <template #cardBody>
      <div class="table-responsive row">
        <table class="table align-middle table-row-bordered fs-6 gy-3 gx-4"
                  id="invoices_datatable">
            <thead class="mb-5">
              <tr class="fw-bold fs-6" style="background-color: #F2F3F4;">
                <th class="min-w-100px text-center">{{ $t('invoice.label.date') }}</th>
                <th class="min-w-100px text-center">{{ $t('invoice.label.number') }}</th>
                <th class="min-w-100px text-center">{{ $t('invoice.label.status') }} </th>
                <th class="min-w-100px text-center">{{ $t('invoice.label.totalAmount') }}</th>
                <th class="w-100px text-start">{{ $t('label.action') }}</th>
              </tr>
            </thead>
        </table>
      </div>
    </template>
  </Card>
</template>

<script setup>
import {computed, nextTick, onMounted, ref, watch} from "vue";
import {useStore} from "vuex";
import i18n from "@/plugins/i18n";
import Ls from "@/services/ls";
import {BASE_URL} from "@/services/api";
import {formattedLocalAmount} from "@/mixins/Helpers";
import {useRouter, useRoute} from "vue-router";
import {useLocales} from "@/composables/locales";
import {useHasAbility} from "@/composables/hasAbility";
import DateRangePicker from "@/components/DateRangePicker.vue";
import ButtonDropdownFilter from "@/components/ButtonDropdownFilter.vue";
import Card from "@/Modules/CRM/components/Card";
import Button from "@/components/Button.vue";
import {Theme} from "@/composables/theming";
import abilities from "@/composables/abilities";

const router = useRouter()
const route = useRoute()

const store = useStore()
const { can } = useHasAbility()
const {locale, datatableLocaleUrl} = useLocales()

const props = defineProps({
  pipeline: Object
})

const table = ref()
const query = ref({
  status: 'all',
  start_date: '',
  end_date: ''
})

const invoiceStatus = computed(() => store.getters['invoices/invoiceStatus'])
const paymentPaidStatus = computed(() => invoiceStatus.value.find(el => el.value == 'PAID')?.value || '')
const currency = computed(() => store.getters['company/getCompanyCurrency'])

watch(() => locale.value, (value) => {
  nextTick(() => fetchDatas() )
}, { deep: true })

watch(() => query.value.status, (newValue) => {
  if(table.value)
    table.value.ajax.url(`${BASE_URL}/get/opportunity/${route.params.id}/invoices/datatables?status=${query.value.status}&startDate=${query.value.start_date}&endDate=${query.value.end_date}`).load()
})

watch(() => [query.value.start_date, query.value.end_date], (newValue) => {
  if(table.value)
    table.value.ajax.url(`${BASE_URL}/get/opportunity/${route.params.id}/invoices/datatables?status=${query.value.status}&startDate=${query.value.start_date}&endDate=${query.value.end_date}`).load()
})

function fetchDatas() {
  if(can(abilities.VIEW_INVOICE_OPPORTUNITIES)) {
    const AUTH_TOKEN = Ls.get('auth.token')
    const pageLimit = Ls.get('pageLength')

    table.value = $("#invoices_datatable").DataTable({
      destroy: true,
      responsive: !0,
      searchDelay: 500,
      processing: !0,
      serverSide: !0,
      pageLength: pageLimit ||  5,
      lengthMenu: [5, 10, 25, 50, 75, 100 ],
      order: [[ 0, 'desc']],
      ajax: {
        url: BASE_URL + `/get/opportunity/${route.params.id}/invoices/datatables?status=${query.value.status}&startDate=${query.value.start_date}&endDate=${query.value.end_date}`,
        type: "GET",
        beforeSend: function (request) {
          request.setRequestHeader("Authorization", "Bearer " + AUTH_TOKEN);
          request.setRequestHeader("Company", Ls.get('current.company'));
        }
      },
      columns: [
        { data: "date" }, { data: "invoice_number" }, { data: "invoice_status" }, { data: "total" }, { data: "Actions", responsivePriority: -1 }
      ],
      columnDefs: [

        {
          targets: -5,
          order: "desc",
          className: "dt-center text-center clickable" ,
          render: function (t, a, e, l) {
            return '<span data-id="'+ e.id +'">' + t + "</span>";
          },
        },
        {
          targets: -4,
          order: "desc",
          className: "dt-center text-center clickable" ,
          render: function (t, a, e, l) {
            return '<span data-id="'+ e.id +'">' + t + "</span>";
          },
        },
        {
          targets: -3,
          orderable: false,
          className: "dt-center text-center" ,
          render: function (t, a, e, l) {
            let status = getInvoiceStatusName(t)
            const locale = i18n.global.locale.value
            let color = status.color
            status = status[locale]
            return '<span class="badge badge-light-' + color +'" style="font-size:13px; margin:3px; padding:5px"><span style="margin-left: 5px" class="valueRow' + l.row + ' label label-success label-inline font-weight-bolder mr-2 edit_value_btn">' + status + '</span></span>'
          },
        },
        {
          targets: -2,
          order: "desc",
          className: "dt-center text-center clickable" ,
          render: function (t, a, e, l) {
            return '<span data-id="'+ e.id +'">' + formattedLocalAmount(t) + ' ' + currency.value?.symbol + "</span>";
          },
        },
        {
          width: "10%",
          targets: -1,
          "className": "dt-center text-start",
          title: "Actions",
          orderable: !1,
          render: (t, a, e, l) => {
            let render=""
            if(can(abilities.VIEW_INVOICE) || can(abilities.VIEW_ALL_INVOICE)){
              render+='<a href="javascript:;" data-id="'+ e.id +'"  class="btn btn-sm btn-clean btn-icon show_btn" title="'+ i18n.global.t('button.show') +'"><i  data-id="'+ e.id +'" class="fas fa-eye fs-2" style="color: '+Theme.gray +'"></i></a>'
            }
            if(can(abilities.UPDATE_INVOICE) || can(abilities.UPDATE_ALL_INVOICE)) {
              if(paymentPaidStatus.value !== e.invoice_status){
                render+='<a href="javascript:;" data-id="'+ e.id +'"  class="btn btn-sm btn-clean btn-icon edit_btn" title="'+ i18n.global.t('button.edit') +'"><i  data-id="'+ e.id +'" class="fas fa-edit fs-2"  style="color: '+Theme.gray +'"></i></a>'
              }
            }

            return render
          },
        },

      ],
      language: {
        url: datatableLocaleUrl.value,
      }
    });

    $(document).on("click", "#invoices_datatable .show_btn", (e) => {
      router.push({ name: 'invoices-view', params: { id: e.target.getAttribute('data-id') } })
    });

    $(document).on("click", "#invoices_datatable .edit_btn", (e) => {
      router.push({ name: 'invoices-edit', params: { id: e.target.getAttribute('data-id') } })
    });
  }
}

function getInvoiceStatusName(status) {
  const {color, fr, en} = invoiceStatus.value.find(el => el.value === status) || ''
  return {color, fr, en}
}

function loadData() {
  Promise.all([
    store.dispatch('company/generalInformation'),
    store.dispatch('invoices/loadInvoiceStatus'),
    store.dispatch('contacts/loadContacts'),
    fetchDatas()
  ])
}

onMounted( () => {
  loadData()
})

function handleRangeDate (range) {
  query.value.start_date = range.start_date
  query.value.end_date = range.end_date
}

function openCreateView(){
  let products = props.pipeline.productSaleElements?.map((item) => {
    return {
      id: item.id,
      quantity: 1,
      price: item.selling_price,
      taxes: item.taxes.map(tax => tax.id)
    }
  }) || []
  const data = {
    opportunity_id: props.pipeline.id,
    contact_id: props.pipeline.contact_id,
    products: JSON.stringify(products)
  }

  router.push({
    name: 'invoices-create',
    query: data
  })

}
</script>

<style scoped>

</style>
